import React, { useState, useMemo, useEffect } from "react";
import "./App.css";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Slide,
  Paper,
  Chip,
  Autocomplete,
  Drawer,
  Button,
  List,
  ListItem,
} from "@mui/material";
import { KeyboardArrowDown, Check, HelpOutline, Category } from "@mui/icons-material";
import SquishedBubbles from "./SquishedVisualization";
import logo from "./logo.svg";
import HelpScreen from "./HelpScreen";

import config from "./config";
import { processHighlights } from "./utils";
import MinimalTextField from "./MinimalTextField";
import HighlightEmbed from "./HighlightEmbed";
import PlayPauseButton from "./PlayPauseButton";

const highlights = require(`${config.highlightsJson}`);

function App() {
  const [selectedNode, setSelectedNode] = useState(null);
  const [showArrow, setShowArrow] = useState(false);
  const [showNodeInfo, setShowNodeInfo] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [initiallySelectedHighlight, setInitiallySelectedHighlight] =
    useState(null);
  const [showHelp, setShowHelp] = useState(true);
  const [showTopics, setShowTopics] = useState(false);

  // Autocomplete value
  const [selectedHighlightOption, setSelectedHighlightOption] = useState(null);

  const processedHighlights = useMemo(
    () =>
      processHighlights(
        highlights,
        config.combineConversations,
        config.tagsField
      ),
    []
  );

  const {
    conversationIds,
    conversationToHighlights,
    tagsToHighlights,
    parentTagsToHighlights,
  } = processedHighlights;

  const highlightOptions = useMemo(
    () =>
      highlights.map((highlight) => ({
        label: `${highlight.speaker} (${highlight.id})`,
        id: highlight.id,
      })),
    [highlights]
  );

  useEffect(() => {
    if (initiallySelectedHighlight) {
      const selectedHighlight = highlights.find(
        (highlight) => highlight.id === initiallySelectedHighlight
      );
      if (selectedHighlight) {
        handleNodeSelection(selectedHighlight);
      }
    }
  }, [initiallySelectedHighlight]);

  useEffect(() => {
    // Show help screen on first load
    setShowHelp(true);
  }, []); // Empty dependency array means this runs once on mount

  const handleNodeClick = (node) => {
    handleNodeSelection(node);
  };

  const handleNodeSelection = (node) => {
    setSelectedNode(node);
    setShowArrow(node !== null);

    if (node === null) {
      setShowNodeInfo(false);
      setSelectedTag(null);
      setSelectedHighlightOption(null);
      setIsPlaying(false);
    } else {
      // Find the matching option in highlightOptions
      const matchingOption = highlightOptions.find(
        (option) => option.id === node.id
      );
      setSelectedHighlightOption(matchingOption);

      // After the pan/zoom animation completes (1 second), show the node info panel and start playing
      setTimeout(() => {
        setShowNodeInfo(true);
        setIsPlaying(true);
      }, 1000);
    }
  };

  const toggleNodeInfo = () => {
    setShowNodeInfo((prev) => !prev);
  };

  const handleHighlightSelect = (event, value) => {
    setSelectedHighlightOption(value);
    if (value) {
      setInitiallySelectedHighlight(value.id);
    } else {
      setInitiallySelectedHighlight(null);
    }
  };

  const [isPlaying, setIsPlaying] = useState(false);
  // Add state to track screensaver mode
  const [isScreensaverActive, setIsScreensaverActive] = useState(false);
  const handlePlayToggle = () => {
    setIsPlaying(!isPlaying);
  };

  const toggleHelp = () => {
    setShowHelp(prev => !prev);
  };

  const handleTopicClick = (parentTag) => {
    // Get all highlight indices for this parent tag
    const highlightIndices = parentTagsToHighlights[parentTag];
    if (highlightIndices && highlightIndices.length > 0) {
      // Select a random highlight from the indices
      const randomIndex = highlightIndices[Math.floor(Math.random() * highlightIndices.length)];
      const randomHighlight = highlights[randomIndex];
      // Set initiallySelectedHighlight to trigger the visualization update
      setInitiallySelectedHighlight(randomHighlight.id);
      handleNodeSelection(randomHighlight);
    }
    setShowTopics(false);
  };

  const toggleTopics = () => {
    setShowTopics(prev => !prev);
  };

  return (
    <div className="fullScreen">
      <AppBar position="static" sx={{ backgroundColor: "black" }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <img src={logo} alt="SenseMap Logo" style={{ height: '24px', width: 'auto' }} />
            SenseMap
          </Typography>
          {selectedNode && (
            <PlayPauseButton
              highlight={selectedNode}
              isPlaying={isPlaying}
              onClick={handlePlayToggle}
              size="sm"
            />
          )}
          <Autocomplete
            options={highlightOptions}
            getOptionLabel={(option) => option.label}
            onChange={handleHighlightSelect}
            value={selectedHighlightOption}
            renderInput={(params) => (
              <MinimalTextField
                {...params}
                variant="standard"
                placeholder="Search Highlights"
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: false,
                  sx: {
                    color: "white",
                    fontSize: "0.875rem",
                  },
                }}
              />
            )}
            sx={{
              width: 180,
              "& .MuiAutocomplete-inputRoot": {
                paddingTop: 0,
                paddingBottom: 0,
              },
            }}
          />
          <Button
            color="inherit"
            onClick={toggleTopics}
            sx={{ 
              ml: 2,
              minWidth: '40px',
              height: '40px',
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
          >
            Themes
          </Button>
          <IconButton
            edge="end"
            color="inherit"
            aria-label="help"
            onClick={toggleHelp}
            sx={{ ml: 1 }}
          >
            <HelpOutline />
          </IconButton>
          {showArrow && (
            <IconButton
              edge="end"
              color="inherit"
              aria-label="arrow"
              onClick={toggleNodeInfo}
              className="pulsingArrow"
              sx={{
                position: "absolute",
                right: "50%",
                transform: "translateX(50%)",
              }}
            >
              {showNodeInfo ? (
                <KeyboardArrowDown
                  sx={{ fontSize: 40, transform: "rotate(180deg)" }}
                />
              ) : (
                <KeyboardArrowDown sx={{ fontSize: 40 }} />
              )}
            </IconButton>
          )}
        </Toolbar>
      </AppBar>
      <Drawer
        anchor="right"
        open={showTopics}
        onClose={() => setShowTopics(false)}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            width: 300,
            display: 'flex',
            flexDirection: 'column',
          }
        }}
      >
        <Box sx={{ 
          p: 2,
          flexShrink: 0, // Prevents the header from shrinking
        }}>
          <Typography variant="h6" gutterBottom>
            Themes
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              mb: 2,
              fontSize: '0.875rem',
              lineHeight: 1.4
            }}
          >
            Click a theme to hear voices from related conversations. Follow the connections to discover related highlights.
          </Typography>
        </Box>
        <List sx={{ 
          overflow: 'auto', // Enables scrolling
          flexGrow: 1, // Takes up remaining space
          px: 2, // Horizontal padding
          pb: 2, // Bottom padding
        }}>
          {Object.keys(parentTagsToHighlights).sort().map((parentTag) => (
            <ListItem key={parentTag} sx={{ py: 0.5 }}>
              <Chip
                label={parentTag}
                onClick={() => handleTopicClick(parentTag)}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  },
                  width: '100%',
                  justifyContent: 'flex-start',
                  fontSize: '0.9rem',
                  height: '32px',
                }}
              />
            </ListItem>
          ))}
        </List>
      </Drawer>
      <div className="visualizationContainer">
        <SquishedBubbles
          conversationIds={conversationIds}
          conversationToHighlights={conversationToHighlights}
          parentTagsToHighlights={parentTagsToHighlights}
          onNodeClick={handleNodeClick}
          selectedNode={selectedNode}
          selectedTag={selectedTag}
          setSelectedTag={setSelectedTag}
          initiallySelectedHighlight={initiallySelectedHighlight}
          // Pass a callback to update screensaver state
          onScreensaverActiveChange={setIsScreensaverActive}
        />
        <HelpScreen open={showHelp} onClose={() => setShowHelp(false)} />
      </div>
      <Slide direction="down" in={showNodeInfo}>
        <Paper
          sx={{
            position: "absolute",
            top: 64, // AppBar height
            left: 0,
            right: 0,
            zIndex: 1200, // Ensure it appears above other content
            padding: 3,
            paddingBottom: 1,
            paddingTop: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)", // Semi-transparent black
            borderRadius: 0,
          }}
        >
          {selectedNode && (
            <>
              <HighlightEmbed
                highlight={selectedNode}
                isPlaying={isPlaying}
                setIsPlaying={setIsPlaying}
                muted={isScreensaverActive}
              />
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                }}
              >
                {selectedNode.tags.sort().map((tag) => (
                  <Chip
                    key={tag}
                    icon={tag === selectedTag ? <Check /> : null}
                    label={tag}
                    onClick={() => setSelectedTag(tag === selectedTag ? null : tag)}
                    sx={{
                      backgroundColor:
                        tag === selectedTag
                          ? "rgba(255, 255, 255, 0.8)"
                          : "rgba(255, 255, 255, 0.5)",
                      margin: 0.5,
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                      },
                      opacity: selectedTag && tag !== selectedTag ? 0.5 : 1,
                    }}
                  />
                ))}
              </Box>
            </>
          )}
        </Paper>
      </Slide>
    </div>
  );
}

export default App;
